import { ExportedMessageRepository } from "@assistant-ui/core";
import type { ThreadHistoryAdapter } from "@assistant-ui/core";
import { ConvForbidden, ConvNotFound, fetchConversation } from "./api";
import type { ConversationDetail } from "./types";
import { registerRef } from "./attachments/refRegistry";

type AuthHeader = Record<string, string>;
type FetchConversation = (
  authHeader: AuthHeader,
  id: string,
) => Promise<ConversationDetail>;

export function createConversationHistoryAdapter({
  authHeader,
  conversationId,
  loadHistory = true,
  fetchConversation: fetchConversationFn = fetchConversation,
}: {
  authHeader: AuthHeader;
  conversationId: string | null;
  loadHistory?: boolean;
  fetchConversation?: FetchConversation;
}): ThreadHistoryAdapter {
  return {
    async load() {
      if (!conversationId || !loadHistory) return { messages: [] };
      try {
        const detail = await fetchConversationFn(authHeader, conversationId);
        for (const msg of detail.messages) {
          for (const att of msg.attachments ?? []) {
            registerRef(att);
          }
        }
        return ExportedMessageRepository.fromArray(
          detail.messages.map((m) => {
            const parts: Array<
              | { type: "text"; text: string }
              | { type: "image"; image: string }
              | {
                  type: "file";
                  mimeType: string;
                  data: string;
                }
            > = [];
            if (m.content) parts.push({ type: "text", text: m.content });
            for (const att of m.attachments ?? []) {
              if (att.mime_type.startsWith("image/")) {
                parts.push({ type: "image", image: att.download_url });
              } else {
                parts.push({
                  type: "file",
                  mimeType: att.mime_type,
                  data: att.download_url,
                });
              }
            }
            return {
              role: m.role,
              content:
                parts.length === 0
                  ? [{ type: "text" as const, text: "" }]
                  : parts,
            };
          }),
        );
      } catch (e) {
        if (e instanceof ConvNotFound || e instanceof ConvForbidden) {
          return { messages: [] };
        }
        throw e;
      }
    },
    async append() {
      // Nova OS persists via the AG-UI stream itself; no client mirror.
    },
  };
}
